import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import DataGrid, { DataGridItem } from "components/data-grid";
import { injectIntl } from "react-intl";
import withStyles from "components/with-styles";
import styles from "./styles/source-data.css.js";
import { BasicSourceData, SourcePreview, StatusData, RestartData, AdminData, SecondCoachData } from "./data-page";
import { useMutation } from "@apollo/react-hooks";
import { useLoadingContext } from "components/loading-spinner-overlay";
import { ActionWithModal } from "components/buttons";
import {
  changeSourcePrivacyMutation,
  checkStatusMutation,
  restartSourceMutation,
  changeSourceDisabledMutation,
  changeSourceDisabledIAMutation,
  changeSourceDisabledDewarpMutation,
} from ".";
import { RestrictedToAdmin } from "components/auth";
import { useHistory } from "react-router-dom";
import { useImperativeTracking } from "components/tracking";

import { deleteSecondCoachStreamSource } from "../../../services/commonService";
import Card from "@material-ui/core/Card";
import I18nBoolean from "components/i18n-boolean";
import { TextDisplay } from "components/display-data";
import { LinkWithIcon } from "components/link";
import { Restricted, permissions } from "components/auth";
import { PageSection, PageHeaderBar } from "components/page";
import SourceAdvancedInfo from "./data-page/source-advanced-info.jsx";
import {
  STREAMSOURCE_SERVICE_ACTIONS,
  STREAMSOURCE_SERVICE_ACTION_CHECK_CURRENT_CODIFICATIONS,
  STREAMSOURCE_SERVICE_ACTION_CHECK_CURRENT_CONNECTIONS,
  STREAMSOURCE_SERVICE_ACTION_CHECK_RECORDINGS_IN_PROGRESS,
  STREAMSOURCE_SERVICE_ACTION_CHECK_SERVER_REPORT,
  STREAMSOURCE_SERVICE_ACTION_GET_LOGS,
} from "../../../utils/constants.js";
import {
  downloadStreamsourceLogs,
  downloadStreamsourceSpecificLog,
  getStreamsourceAdvancedInfo,
} from "../../../services/streamsourceServices.js";
const SourceData = ({ classes, source: receivedSource, intl, showCamera = true, descriptionText = null }) => {
  const [source, setSource] = useState(receivedSource);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const { setLoading } = useLoadingContext();
  const track = useImperativeTracking();
  const history = useHistory();

  const [checkSourceStatus] = useMutation(checkStatusMutation);
  const [changeSourcePrivacy] = useMutation(changeSourcePrivacyMutation);
  const [changeSourceDisabled] = useMutation(changeSourceDisabledMutation);
  const [changeSourceDisabledIA] = useMutation(changeSourceDisabledIAMutation);
  const [restartSource] = useMutation(restartSourceMutation);
  const [changeSourceDisabledDewarp] = useMutation(changeSourceDisabledDewarpMutation);

  const [secondCoachUrlEnabled, setSecondCoachUrlEnabled] = useState(!!source.publicInterface.secondCoachUrl);

  const mutationVariables = source ? { sourceId: source.id } : {};

  const handleCheckStatus = () => {
    setLoading(true);
    if (source) {
      track(`User checked source status for [${source.id}]`);
    }
    checkSourceStatus(source ? { variables: mutationVariables } : null)
      .then(({ data }) => setSource(data.checkSourceStatus))
      .finally(() => setLoading(false));
  };

  const handleChangePrivacy = () => {
    setLoading(true);
    if (source) {
      track(`User changed source privacy status for [${source.id}] to [${source.privacyActivated}]`);
    }
    changeSourcePrivacy(source ? { variables: { ...mutationVariables, privacy: !source.privacyActivated } } : null)
      .then(({ data }) => setSource({ ...source, ...data.changeSourcePrivacy }))
      .finally(() => setLoading(false));
  };

  const handleChangeDisabled = () => {
    setLoading(true);
    if (source) {
      track(`User changed source disabled status for [${source.id}] to [${source.disabled}]`);
    }
    changeSourceDisabled(source ? { variables: { ...mutationVariables, disabled: !source.disabled } } : null)
      .then(({ data }) => setSource({ ...source, ...data.changeSourceDisabled }))
      .finally(() => setLoading(false));
  };

  const handleChangeDisabledIA = () => {
    setLoading(true);
    if (source) {
      track(`User changed source iaDisabled status for [${source.id}] to [${source.iaDisabled}]`);
    }
    changeSourceDisabledIA(source ? { variables: { ...mutationVariables, disabledIA: !source.iaDisabled } } : null)
      .then(({ data }) => setSource({ ...source, ...data.changeSourceDisabledIA }))
      .finally(() => setLoading(false));
  };

  const handleChangeDisabledDewarp = () => {
    setLoading(true);
    if (source) {
      track(`User changed source dewarpDisabled status for [${source.id}] to [${source.dewarpDisabled}]`);
    }
    changeSourceDisabledDewarp(
      source ? { variables: { ...mutationVariables, dewarpDisabled: !source.dewarpDisabled } } : null,
    )
      .then(({ data }) => setSource({ ...source, ...data.changeSourceDisabledDewarp }))
      .finally(() => setLoading(false));
  };

  const handleShowModalConfirmationSecondCoach = () => {
    setShowValidateModal(true);
  };

  const hideModalConfirmationSecondCoach = response => {
    if (response) {
      setLoading(false);
      setShowValidateModal(false);
      setSecondCoachUrlEnabled(false);
      history.push(`/source/details/${source.id}`);
    }
  };

  const deleteSecondCoach = () => {
    setLoading(true);
    deleteSecondCoachStreamSource({ streamsourceId: source.id }, hideModalConfirmationSecondCoach);
    hideModalConfirmationSecondCoach();
  };

  const content = intl.formatMessage(
    { id: descriptionText || "app.dialog.delete.text" },
    {
      element: intl.formatMessage({ id: "app.dialog.delete.elementToDelete.default" }),
    },
  );

  const handleRestartSource = () => {
    setLoading(true);
    if (source) {
      track(`User restarted source [${source.id}]`);
    }
    restartSource(source ? { variables: mutationVariables } : null).finally(() => setLoading(false));
  };

  // source advanced info & logs

  const [sourceAllLogs, setSourceAllLogs] = useState(null);
  const [sourceLog, setSourceLog] = useState(null);

  const [sourceOperationCheckServerReport, setSourceOperationCheckServerReport] = useState(null);
  const [sourceOperationCheckCurrentCodifications, setSourceOperationCheckCurrentCodifications] = useState(
    intl.formatMessage({ id: "common.nodata" }),
  );
  const [sourceOperationCheckCurrentConnections, setSourceOperationCheckCurrentConnections] = useState(
    intl.formatMessage({ id: "common.nodata" }),
  );
  const [sourceOperationCheckRecordingsInProgress, setSourceOperationCheckRecordingsInProgress] = useState(
    intl.formatMessage({ id: "common.nodata" }),
  );

  const handlerSetSourceAllLogs = data => {
    setSourceAllLogs(data);
  };
  const handlerSetSourceLog = data => {
    setSourceLog(data);
  };

  const handlerSetSourceOperationCheckServerReport = data => {
    setSourceOperationCheckServerReport(data);
  };
  const handlerSetSourceOperationCheckCurrentCodifications = data => {
    setSourceOperationCheckCurrentCodifications(data);
  };
  const handlerSetSourceOperationCheckCurrentConnections = data => {
    setSourceOperationCheckCurrentConnections(data);
  };
  const handlerSetSourceOperationCheckRecordingsInProgress = data => {
    if (data.length === 0) {
      setSourceOperationCheckRecordingsInProgress(intl.formatMessage({ id: "source.details.noRecordingInProgress" }));
    } else {
      setSourceOperationCheckRecordingsInProgress(intl.formatMessage({ id: "source.details.recordingInProgress" }));
    }
  };

  const handlerSetActionServices = action => {
    switch (action) {
      case STREAMSOURCE_SERVICE_ACTION_CHECK_SERVER_REPORT:
        return handlerSetSourceOperationCheckServerReport;
      case STREAMSOURCE_SERVICE_ACTION_CHECK_CURRENT_CONNECTIONS:
        return handlerSetSourceOperationCheckCurrentConnections;
      case STREAMSOURCE_SERVICE_ACTION_CHECK_CURRENT_CODIFICATIONS:
        return handlerSetSourceOperationCheckCurrentCodifications;
      case STREAMSOURCE_SERVICE_ACTION_CHECK_RECORDINGS_IN_PROGRESS:
        return handlerSetSourceOperationCheckRecordingsInProgress;
      default:
        null;
        break;
    }
  };

  useEffect(
    () => {
      if (source && source.id) {
        STREAMSOURCE_SERVICE_ACTIONS.map(action => {
          getStreamsourceAdvancedInfo({ streamsourceId: source.id, action: action }, () => handlerSetActionServices(action));
        });
        downloadStreamsourceLogs({ streamsourceId: source.id }, handlerSetSourceAllLogs);
      }
    },
    [source],
  );

  useEffect(
    () => {
      if (sourceAllLogs) {
        downloadStreamsourceSpecificLog(
          { streamsourceId: source.id, logfile_id: sourceAllLogs[0] },
          handlerSetSourceLog,
        );
      }
    },
    [sourceAllLogs],
  );

  // date from file name (log file name format: "serial-date-type.txt")
  const date = new Date()
    .toLocaleDateString("es-ES", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour12: false,
      timeZone: "UTC",
    })
    .split("/")
    .reverse()
    .join("-");

  return !source ? null : (
    <>
      <PageSection spaced>
        <DataGrid>
          <DataGridItem container>
            <BasicSourceData
              source={source}
              onChangePrivacy={handleChangePrivacy}
              onChangeDisabled={handleChangeDisabled}
              onChangeDisabledIA={handleChangeDisabledIA}
              onChangeDisabledDewarp={handleChangeDisabledDewarp}
            />
          </DataGridItem>
          {showCamera ? (
            <DataGridItem container>
              <SourcePreview source={source} showCamera={showCamera} />
            </DataGridItem>
          ) : (
            <DataGridItem container>
              <StatusData source={source} onCheckStatus={handleCheckStatus} />
              <RestartData source={source} onRestartSource={handleRestartSource} />
            </DataGridItem>
          )}
        </DataGrid>
      </PageSection>
      <PageHeaderBar subtitle title="app.page.source.title.avancedData" />
      <DataGrid>
        <Card className={classes.dataCard}>
          <DataGrid>
            <DataGridItem>
              {source.deployment && (
                <LinkWithIcon
                  text={source.deployment.codename}
                  iconName="settings-input-antenna"
                  color="primary"
                  href={`/deployment/details/${source.deployment.id}`}
                />
              )}
            </DataGridItem>
            {source.publicInterface.ptzControllerPort ? (
              <DataGridItem>
                <TextDisplay label={intl.formatMessage({ id: "source.ptzControllerPort" })}>
                  {source.publicInterface.ptzControllerPort}
                </TextDisplay>
              </DataGridItem>
            ) : (
              <DataGridItem />
            )}
            <DataGridItem>
              <TextDisplay label={intl.formatMessage({ id: "source.firmwareUpdateAvailable" })}>
                <I18nBoolean value={source.firmwareUpdateAvailable} />
              </TextDisplay>
            </DataGridItem>
            <DataGridItem>
              <TextDisplay label={intl.formatMessage({ id: "source.currentFirmware" })}>
                {source.currentFirmware}
              </TextDisplay>
            </DataGridItem>

            <DataGridItem>
              <TextDisplay label={intl.formatMessage({ id: "source.deploymentNeeds" })}>
                {source.deploymentNeeds}
              </TextDisplay>
            </DataGridItem>
            {source.usuallyDisconnected ? (
              <DataGridItem>
                <TextDisplay label={intl.formatMessage({ id: "source.usually.disconnected.label" })}>
                  {intl.formatMessage({ id: "source.usually.disconnected" })}
                </TextDisplay>
              </DataGridItem>
            ) : (
              <DataGridItem />
            )}
          </DataGrid>
          <DataGridItem container>
            <Restricted permissions={[permissions.source_view_advanced]}>
              <>
                {source?.users?.admin && (
                  <DataGrid>
                    <DataGridItem>
                      <TextDisplay label={intl.formatMessage({ id: "source.admin" })}>
                        {source.users.admin.user}
                      </TextDisplay>
                    </DataGridItem>
                    <DataGridItem>
                      <TextDisplay label={intl.formatMessage({ id: "source.admin.pass" })}>
                        {source.users.admin.pass}
                      </TextDisplay>
                    </DataGridItem>
                  </DataGrid>
                )}
                {source?.users?.viewer && (
                  <DataGrid>
                    <DataGridItem>
                      <TextDisplay label={intl.formatMessage({ id: "source.viewer" })}>
                        {source.users.viewer.user}
                      </TextDisplay>
                    </DataGridItem>
                    <DataGridItem>
                      <TextDisplay label={intl.formatMessage({ id: "source.viewer.pass" })}>
                        {source.users.viewer.pass}
                      </TextDisplay>
                    </DataGridItem>
                  </DataGrid>
                )}
              </>
            </Restricted>
          </DataGridItem>
        </Card>
      </DataGrid>
      <PageHeaderBar subtitle title="app.page.source.title.statusData" />
      <DataGrid>
        {showCamera && (
          <Card className={classes.dataCard}>
            <DataGridItem container big>
              <StatusData source={source} onCheckStatus={handleCheckStatus} />
              <RestartData source={source} onRestartSource={handleRestartSource} />
              <SourceAdvancedInfo
                source={source}
                intl={intl}
                sourceOperationCheckServerReport={sourceOperationCheckServerReport}
                sourceOperationCheckCurrentConnections={sourceOperationCheckCurrentConnections}
                sourceOperationCheckCurrentCodifications={sourceOperationCheckCurrentCodifications}
                sourceOperationCheckRecordingsInProgress={sourceOperationCheckRecordingsInProgress}
                sourceLog={sourceLog}
                dateText={date}
              />
            </DataGridItem>
          </Card>
        )}
      </DataGrid>
      <PageHeaderBar subtitle title="app.page.source.title.conectionData" />
      <DataGrid>
        <Card className={classes.dataCard}>
          <DataGridItem container>
            <SecondCoachData
              secondCoachUrlEnabled={secondCoachUrlEnabled}
              source={source}
              actionShowModalConfirmationSecondCoach={handleShowModalConfirmationSecondCoach}
            />
          </DataGridItem>
          <DataGridItem container>
            <RestrictedToAdmin>
              <AdminData source={source} />
            </RestrictedToAdmin>
          </DataGridItem>
        </Card>
      </DataGrid>

      {showValidateModal && (
        <ActionWithModal
          actionButton={true}
          dialogTitle={intl.formatMessage({ id: "app.dialog.delete.title" })}
          renderDialogContent={() => content}
          icon="delete"
          onSuccess={deleteSecondCoach}
          open={true}
          mutation={null}
          onAccept={deleteSecondCoach}
        />
      )}
    </>
  );
};

SourceData.propTypes = {
  /**
   * Mostrar imagen de cámara
   **/
  showCamera: PropTypes.bool,
  /**
   * Data to represent
   **/
  source: PropTypes.object,
};

export default injectIntl(withStyles(styles)(SourceData));
